// Per-recording dismissals for the cross-device recording-recovery banner.
// Stored in localStorage keyed by viewer (login email via recordingViewerKey)
// so two operators on the same machine don't share their dismissals. Sibling
// of peptideRestockAckState.js — same storage mechanism, different concern.
//
// Why it has to persist: clicking × on the banner is the user saying "I've
// dealt with this recording, stop nudging me about it." Previously the
// component held the dismissed ids in an in-memory Set (useRef), so the × only
// suppressed the banner for the current page session — a refresh or navigation
// dropped the Set and the same banner came right back. That made the dismiss
// feel broken. Persisting the ids to localStorage makes the dismissal stick
// across reloads, per-browser.
//
// Shape: a recency-ordered array of recordingId strings, oldest first and the
// most-recently-dismissed last. We keep only the newest MAX_IDS so the list
// can't grow without bound on a long-lived browser.
//
// The cap used to be 50, justified by "a dismissed id only needs to be remembered
// until the backend sweep purges that recording's manifest (48h / 14d), so this is
// a safety rail, not a functional limit." That reasoning depends on the sweep
// actually running the current policy. When it does not — as when the recording
// process set is behind the client — pending rows never purge, a user accumulates
// far more than 50, and pure-recency eviction silently resurfaces the OLDEST
// dismissed banner on dismissal 51. Dismissing then feels broken again, which is
// the exact bug this store exists to fix.
//
// So the cap is now sized to survive that failure mode rather than to assume it
// away. Ids are UUID strings; even at this size the serialized list is a few tens
// of KB, far inside any localStorage budget.

import { recordingViewerKey } from "./recordingViewerKey";

const STORAGE_KEY_PREFIX = "recordingRecoveryDismissed:";
// Exported so tests exercise the eviction MECHANISM rather than pinning a literal —
// the previous tests hard-coded 50 and would fail on any retune of a value whose
// exact number is not the contract.
export const MAX_IDS = 1000;

function storageKey() {
  return STORAGE_KEY_PREFIX + recordingViewerKey();
}

function readList() {
  try {
    const key = storageKey();
    let raw = localStorage.getItem(key);
    if (raw == null && !key.endsWith(":anonymous")) {
      // One-time adoption of the legacy shared bucket. Builds before
      // recordingViewerKey scoped every dismissal under "anonymous" (the old
      // per-store helper read an `auth` key nothing writes), so without this a
      // deploy would resurface every banner the user had already dismissed.
      // Copy, don't move — another operator on this machine adopts the same
      // legacy list into their own bucket. The ids are opaque recording UUIDs.
      raw = localStorage.getItem(STORAGE_KEY_PREFIX + "anonymous");
      if (raw != null) localStorage.setItem(key, raw);
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Defend against a corrupt/legacy value: only accept an array of strings,
    // anything else degrades to "nothing dismissed yet" rather than throwing.
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function writeList(list) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(list));
  } catch {
    // Quota / private-mode failures are silent — dismiss state is best-effort.
  }
}

// Hydrate the dismissed-recording Set for the current viewer. The banner and
// the cross-device hook re-read this at the top of every poll (60s cadence),
// so a dismissal made by EITHER surface — or by another tab — suppresses the
// recording everywhere within one poll cycle instead of only after a reload.
export function loadDismissedRecordingIds() {
  return new Set(readList());
}

// Persist a dismissal. Re-inserting an already-known id moves it to the most-
// recent position so the prune keeps the freshest dismissals, then caps the
// stored list to the newest MAX_IDS.
export function persistDismissedRecordingId(recordingId) {
  if (!recordingId || typeof recordingId !== "string") return;
  const list = readList().filter((id) => id !== recordingId);
  list.push(recordingId);
  writeList(list.length > MAX_IDS ? list.slice(list.length - MAX_IDS) : list);
}
